import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBagShopping,
  faBars,
  faBolt,
  faChevronDown,
  faChevronLeft,
  faClock,
  faClockRotateLeft,
  faComments,
  faCreditCard,
  faEnvelope,
  faGift,
  faHeadset,
  faHouse,
  faKey,
  faLayerGroup,
  faListCheck,
  faPhone,
  faRightFromBracket,
  faUser,
} from '@fortawesome/free-solid-svg-icons';

/**
 * Every icon in the app, in one place.
 *
 * Each export is a component with the same prop surface the previous
 * Ant Design icons had (`className`, `style`, `aria-hidden`), so call
 * sites did not have to change shape when the icon library was swapped.
 *
 * Keeping the mapping here rather than importing FontAwesome directly at
 * each call site means a future icon change is one edit, and it stops two
 * pages drifting to different icons for the same concept.
 */

/**
 * `style` is borrowed from FontAwesomeIcon rather than declared as
 * React.CSSProperties: FontAwesome 7 types it as `CSSProperties &
 * CSSVariables` so callers can set `--fa-*` custom properties, and a plain
 * CSSProperties is not assignable to that intersection.
 */
type IconStyle = React.ComponentProps<typeof FontAwesomeIcon>['style'];

interface IconProps {
  className?: string;
  style?: IconStyle;
  'aria-hidden'?: boolean | 'true' | 'false';
}

const make = (icon: IconDefinition): React.FC<IconProps> => {
  const Wrapped: React.FC<IconProps> = ({ className, style, ...rest }) => (
    <FontAwesomeIcon icon={icon} className={className} style={style} {...rest} />
  );
  return Wrapped;
};

// Primary navigation
export const HomeIcon = make(faHouse);
export const DetailIcon = make(faBagShopping);
export const StartIcon = make(faBolt);
export const SupportIcon = make(faHeadset);
export const LuckySpinIcon = make(faGift);
export const AccountIcon = make(faUser);

// Account services
export const BankIcon = make(faCreditCard);
export const WithdrawalHistoryIcon = make(faClockRotateLeft);
export const RulesIcon = make(faListCheck);
export const PlatformIcon = make(faLayerGroup);
export const PasswordIcon = make(faKey);
export const LogoutIcon = make(faRightFromBracket);

// Support / contact
export const BusinessHoursIcon = make(faClock);
export const PhoneIcon = make(faPhone);
export const EmailIcon = make(faEnvelope);
export const LiveChatIcon = make(faComments);

// Controls
export const ChevronDownIcon = make(faChevronDown);
export const ChevronLeftIcon = make(faChevronLeft);
export const MenuIcon = make(faBars);
